import * as THREE from "three";
import { useMemo, useCallback, memo, useRef, useEffect } from "react";
import type { Direction, Point } from "../types/directionsTypes";
import type { FloorVisibility } from "../utils/floorVisibility";
import { getFloorFromY } from "../utils/floorVisibility";

// =============================================================================
// КОНСТАНТЫ
// =============================================================================

/** Точность координат для ключей (знаков после запятой) */
const COORD_PRECISION = 4;

/** Порог dot product для объединения runs (близость направлений) */
const DIRECTION_SIMILARITY_THRESHOLD = 0.9995;

/** Порог вертикальности для коррекции roll (|dir·up| < threshold) */
const VERTICAL_THRESHOLD = 0.985;

/** Минимальная длина для числовых проверок */
const EPSILON = 1e-9;

/** Квадрат epsilon для lengthSq проверок */
const EPSILON_SQ = 1e-10;

// =============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// =============================================================================

/**
 * Генерирует строковый ключ координат для сопоставления точек.
 *
 * Система координат:
 * - direction.points: оригинальные (x, y, z) из бэкенда
 * - points/pts (Vector3): преобразованные [x, z, -y] (см. scene.tsx)
 *
 * При вызове из direction.points: getCoordKey(pt.x, pt.z, -pt.y)
 * При вызове из pts (Vector3): getCoordKey(p.x, p.y, p.z)
 * Оба варианта дают одинаковый ключ.
 */
const getCoordKey = (x: number, y: number, z: number): string =>
  `${x.toFixed(COORD_PRECISION)},${y.toFixed(COORD_PRECISION)},${z.toFixed(COORD_PRECISION)}`;

// =============================================================================
// КОМПОНЕНТ
// =============================================================================

interface RouteLineProps {
  points: [number, number, number][];
  direction: Direction | null;
  selectedSegmentId: number | null;
  // Геометрия плашек
  gapLength?: number;
  width?: number;
  height?: number;
  length?: number;
  // Gap между сегментами (визуальное разделение)
  segmentGap?: number;
  // Цвета
  activeColor?: string;
  dimmedColor?: string;
  dimmedOpacity?: number;
  // Настройки сглаживания
  cornerPadding?: number;
  turnDotThreshold?: number;
  minDashLength?: number;
  // Видимость этажей
  floorVisibility?: FloorVisibility;
}

const RouteLine = memo(function RouteLine({
  points,
  direction,
  selectedSegmentId,
  gapLength = 0.2,
  width = 0.3,
  height = 0.05,
  length = 0.6,
  segmentGap = 0.3,
  activeColor = "#8A5EFF",
  dimmedColor = "#8A5EFF",
  dimmedOpacity = 0.4,
  cornerPadding = 0.01,
  turnDotThreshold = DIRECTION_SIMILARITY_THRESHOLD,
  minDashLength,
  floorVisibility,
}: RouteLineProps) {
  // Рефы для InstancedMesh
  const activeInstanceRef = useRef<THREE.InstancedMesh>(null);
  const overlayInstanceRef = useRef<THREE.InstancedMesh>(null);
  const dimmedInstanceRef = useRef<THREE.InstancedMesh>(null);
  const dimmedActiveInstanceRef = useRef<THREE.InstancedMesh>(null);

  // Кэшированные объекты для updateInstancedMesh (избегаем создания новых на каждый вызов)
  const tempMatrixRef = useRef(new THREE.Matrix4());
  const tempScaleRef = useRef(new THREE.Vector3());
  // Типы для dash-элементов
  type Dash = {
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
    dir: THREE.Vector3;
    len: number;
    isActive: boolean; // новое поле — принадлежит ли активному сегменту
    floor: number; // этаж даша (для определения видимости)
  };

  const dashes: Dash[] = useMemo(() => {
    if (!points || points.length < 2) return [];

    // Если сегмент не выбран (selectedSegmentId === null) — все точки активны
    const allActive = selectedSegmentId === null;

    // ========================================================================
    // 2. МАППИНГ КООРДИНАТ НА ИМЕНА ТОЧЕК
    // ========================================================================

    // Создаём карту: координаты → имя точки
    const coordToName = new Map<string, string>();
    if (direction) {
      for (const pt of direction.points) {
        // Преобразуем из оригинальных координат direction в формат Vector3
        const key = getCoordKey(pt.x, pt.z, -pt.y);
        coordToName.set(key, pt.meshName);
      }
    }

    // ========================================================================
    // 2.5. МАППИНГ ТОЧЕК НА СЕГМЕНТЫ
    // ========================================================================

    // Карта: meshName -> Set<segmentId> (точка может принадлежать нескольким сегментам)
    const pointToSegmentIds = new Map<string, Set<number>>();
    if (direction) {
      for (const segment of direction.segmentationRoute.segments) {
        const addPoint = (name: string | null) => {
          if (!name) return;
          if (!pointToSegmentIds.has(name)) {
            pointToSegmentIds.set(name, new Set());
          }
          pointToSegmentIds.get(name)!.add(segment.id);
        };

        for (const name of segment.pointMeshNames) {
          addPoint(name);
        }
      }
    }

    // ========================================================================
    // 3. ПОСТРОЕНИЕ БАЗОВОЙ ПОЛИЛИНИИ
    // ========================================================================

    // Входные точки (Vector3)
    const pts: THREE.Vector3[] = points.map((p) => new THREE.Vector3(...p));

    // Предварительно вычисляем segmentIds для всех точек (O(n) вместо O(n²))
    const ptsSegmentIds: Set<number>[] = pts.map((p) => {
      const key = getCoordKey(p.x, p.y, p.z);
      const name = coordToName.get(key);
      return name ? (pointToSegmentIds.get(name) ?? new Set()) : new Set();
    });

    // Функция: получить все segmentIds по индексу точки (O(1))
    const getSegmentIds = (index: number): Set<number> => ptsSegmentIds[index];

    // Функция: проверить, является ли точка смежной (принадлежит >1 сегменту)
    const isJunctionPoint = (index: number): boolean =>
      ptsSegmentIds[index].size > 1;

    // Сегменты базовой полилинии (направление и длина между соседними точками)
    const segDirs: THREE.Vector3[] = [];
    const segLens: number[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const v = pts[i + 1].clone().sub(pts[i]);
      const L = v.length();
      if (L > EPSILON) {
        segDirs.push(v.clone().normalize());
        segLens.push(L);
      } else {
        segDirs.push(new THREE.Vector3(0, 0, 1));
        segLens.push(0);
      }
    }

    // ========================================================================
    // 4. ОБЪЕДИНЕНИЕ В ПРОЛЁТЫ (RUNS)
    // ========================================================================

    // Пролёт (run) — последовательность точек с одинаковым направлением в рамках одного сегмента
    type Run = {
      points: THREE.Vector3[];
      segLens: number[];
      length: number;
      segmentId: number | null; // ID сегмента для этого пролёта
    };

    const runs: Run[] = [];
    let i = 0;

    while (i < pts.length - 1) {
      let j = i + 1;
      let baseDir = pts[j].clone().sub(pts[i]).normalize();

      // Определяем segmentId для этого run
      const startSegIds = getSegmentIds(i);
      let runSegmentId: number | null = null;

      if (startSegIds.size === 1) {
        runSegmentId = [...startSegIds][0];
      } else if (startSegIds.size > 1 && j < pts.length) {
        // Первая точка смежная - определяем сегмент по следующей точке
        const nextSegIds = getSegmentIds(j);
        const common = [...startSegIds].filter((id) => nextSegIds.has(id));
        runSegmentId = common.length > 0 ? common[0] : [...startSegIds][0];
      }

      // Объединяем последовательные сегменты с похожим направлением В РАМКАХ ОДНОГО СЕГМЕНТА
      while (j < pts.length - 1) {
        const nextDir = pts[j + 1].clone().sub(pts[j]).normalize();
        const dot = THREE.MathUtils.clamp(baseDir.dot(nextDir), -1, 1);

        // Если текущая точка смежная - это граница, разрыв!
        if (isJunctionPoint(j)) {
          break;
        }

        // Проверяем смену сегмента
        const nextSegIds = getSegmentIds(j + 1);
        const segmentChanges =
          runSegmentId !== null && !nextSegIds.has(runSegmentId);

        if (dot >= turnDotThreshold && !segmentChanges) {
          j++;
          const newDir = pts[j].clone().sub(pts[i]).normalize();
          if (isFinite(newDir.length())) baseDir = newDir;
        } else {
          break; // разрыв: поворот ИЛИ смена сегмента
        }
      }

      const runPts = pts.slice(i, j + 1);
      const runSegLens: number[] = [];
      let runLen = 0;
      for (let k = 0; k < runPts.length - 1; k++) {
        const L = runPts[k + 1].distanceTo(runPts[k]);
        runSegLens.push(L);
        runLen += L;
      }

      if (runLen > EPSILON) {
        runs.push({
          points: runPts,
          segLens: runSegLens,
          length: runLen,
          segmentId: runSegmentId,
        });
      }

      i = j;
    }

    if (runs.length === 0) return [];

    // ========================================================================
    // 5. ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
    // ========================================================================

    // Функция: позиция и направление на расстоянии s от начала пролёта
    const posAt = (run: Run, s: number) => {
      let acc = 0;
      for (let k = 0; k < run.segLens.length; k++) {
        const L = run.segLens[k];
        if (s <= acc + L || k === run.segLens.length - 1) {
          const t = L > EPSILON ? (s - acc) / L : 0;
          const p = run.points[k]
            .clone()
            .lerp(run.points[k + 1], THREE.MathUtils.clamp(t, 0, 1));
          const dir = run.points[k + 1].clone().sub(run.points[k]).normalize();
          return { p, dir };
        }
        acc += L;
      }
      // fallback — конец пролёта
      const lastIdx = run.points.length - 2;
      const p = run.points[lastIdx + 1].clone();
      const dir = run.points[lastIdx + 1]
        .clone()
        .sub(run.points[lastIdx])
        .normalize();
      return { p, dir };
    };

    // Функция: вычисление кватерниона с минимальным поворотом от предыдущего
    const worldUp = new THREE.Vector3(0, 1, 0);
    const altUp = new THREE.Vector3(1, 0, 0);
    const verticalDotThreshold = VERTICAL_THRESHOLD;

    const computeQuaternion = (
      direction: THREE.Vector3,
      prevQ?: THREE.Quaternion,
    ) => {
      let q: THREE.Quaternion;

      if (prevQ) {
        // Минимальный поворот от предыдущей ориентации
        const qAlign = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, 0, 1).applyQuaternion(prevQ),
          direction,
        );
        q = qAlign.clone().multiply(prevQ);
      } else {
        // Базовая ориентация
        const dir = direction.clone();
        let up = worldUp
          .clone()
          .sub(dir.clone().multiplyScalar(worldUp.dot(dir)));
        if (up.lengthSq() < EPSILON_SQ) {
          up = altUp.clone().sub(dir.clone().multiplyScalar(altUp.dot(dir)));
        }
        up.normalize();
        const right = new THREE.Vector3().crossVectors(up, dir).normalize();
        up = new THREE.Vector3().crossVectors(dir, right).normalize();
        const m = new THREE.Matrix4().makeBasis(right, up, dir);
        q = new THREE.Quaternion().setFromRotationMatrix(m);
      }

      // Корректировка roll для горизонтальных участков
      const verticality = Math.abs(direction.dot(worldUp));
      if (verticality < verticalDotThreshold) {
        const upDesired = worldUp
          .clone()
          .sub(direction.clone().multiplyScalar(worldUp.dot(direction)))
          .normalize();
        const upCurrent = new THREE.Vector3(0, 1, 0)
          .applyQuaternion(q)
          .normalize();
        const cross = new THREE.Vector3().crossVectors(upCurrent, upDesired);
        const sin = THREE.MathUtils.clamp(direction.dot(cross), -1, 1);
        const cos = THREE.MathUtils.clamp(upCurrent.dot(upDesired), -1, 1);
        const roll = Math.atan2(sin, cos);
        if (Math.abs(roll) > 1e-6) {
          const qRoll = new THREE.Quaternion().setFromAxisAngle(
            direction,
            roll,
          );
          q = qRoll.multiply(q);
        }
      }
      return q;
    };

    // ========================================================================
    // 6. ГЕНЕРАЦИЯ DASH-ЭЛЕМЕНТОВ
    // ========================================================================

    const out: Dash[] = [];
    const cp = Math.max(0, cornerPadding);
    const nominalLen = Math.max(1e-6, length);
    const minLen = Math.max(
      1e-6,
      minDashLength ?? Math.max(0.06, nominalLen * 0.25),
    );

    let prevQ: THREE.Quaternion | undefined;

    for (let r = 0; r < runs.length; r++) {
      const run = runs[r];
      const prevRun = r > 0 ? runs[r - 1] : null;

      // Определяем, это новый сегмент или продолжение предыдущего
      const isNewSegment =
        prevRun !== null && prevRun.segmentId !== run.segmentId;

      const endLimit = Math.max(0, run.length - cp);
      // Увеличенный отступ в начале нового сегмента для визуального разделения
      let lead =
        r === 0 ? 0 : isNewSegment ? segmentGap : Math.max(0, gapLength - cp);

      // Нет места даже под минимум — пропускаем пролёт
      if (endLimit - lead < minLen) continue;

      // Определяем активность: всё активно ИЛИ этот сегмент выбран
      const dashIsActive = allActive || run.segmentId === selectedSegmentId;

      while (lead <= endLimit - minLen + EPSILON) {
        let len = nominalLen;

        // Укорачиваем, если выходим за границу пролёта
        if (lead + len > endLimit) len = endLimit - lead;

        // Если меньше минимума — прекращаем
        if (len < minLen - EPSILON) break;

        // Центр и направление по середине плашки
        const mid = lead + 0.5 * len;
        const { p, dir } = posAt(run, mid);

        const q = computeQuaternion(dir, prevQ);
        prevQ = q.clone();

        out.push({
          position: p,
          quaternion: q,
          dir: dir.clone(),
          len,
          isActive: dashIsActive,
          floor: getFloorFromY(p.y),
        });

        // Следующий видимый край — через ровно gapLength
        lead = lead + len + gapLength;
      }
    }

    return out;
  }, [
    points,
    direction,
    selectedSegmentId,
    gapLength,
    segmentGap,
    length,
    cornerPadding,
    turnDotThreshold,
    minDashLength,
  ]);

  // ========================================================================
  // 7. ГЕОМЕТРИЯ И МАТЕРИАЛЫ (хуки должны быть до раннего return)
  // ========================================================================

  // Базовая геометрия (единичный куб по Z)
  const baseGeometry = useMemo(
    () => new THREE.BoxGeometry(width, height, 1),
    [width, height],
  );

  // Материалы
  const activeMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: activeColor,
        toneMapped: false, // Важно для bloom эффекта
      }),
    [activeColor],
  );

  // Материал для overlay режима - рисуется поверх всего
  const overlayMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: activeColor,
        toneMapped: false,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.9,
      }),
    [activeColor],
  );

  // Материал для дашей на затемнённых этажах
  const dimmedMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: dimmedColor,
        roughness: 1,
        metalness: 0,
        transparent: true,
        opacity: dimmedOpacity,
      }),
    [dimmedColor, dimmedOpacity],
  );

  // Материал для floor_selected режима (фиолетовый с opacity = 0.5)
  const dimmedActiveMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: activeColor,
        roughness: 1,
        metalness: 0,
        transparent: true,
        opacity: 0.5,
      }),
    [activeColor],
  );

  // Мемоизированная функция определения видимости даша на основе режима и этажа
  const getDashVisibility = useCallback(
    (isActive: boolean): "visible" | "dimmed" | "hidden" => {
      if (!floorVisibility) return "visible";

      const mode = floorVisibility.mode;

      // Режим full_route — весь маршрут видим
      if (mode === "full_route") {
        return "visible";
      }

      // Режим floor_selected — вся линия dimmed
      if (mode === "floor_selected") {
        return "dimmed";
      }

      // Режим segment — активные видны, неактивные dimmed
      if (mode === "segment") {
        return isActive ? "visible" : "dimmed";
      }

      // Fallback (не должен достигаться — RouteLine рендерится только с маршрутом)
      return "visible";
    },
    [floorVisibility],
  );

  // ========================================================================
  // 8. ГРУППИРОВКА ДАШЕЙ ПО ТИПАМ (для InstancedMesh)
  // ========================================================================

  const groupedDashes = useMemo(() => {
    const active: typeof dashes = [];
    const overlay: typeof dashes = [];
    const dimmed: typeof dashes = [];
    const dimmedActive: typeof dashes = [];

    for (const dash of dashes) {
      const visibility = getDashVisibility(dash.isActive);

      if (visibility === "hidden") continue;

      if (visibility === "dimmed") {
        if (floorVisibility?.mode === "floor_selected") {
          dimmedActive.push(dash);
        } else {
          dimmed.push(dash);
        }
        continue;
      }

      // visibility === "visible"
      if (dash.isActive) {
        active.push(dash);
        overlay.push(dash); // Активные также рендерятся в overlay слое
      }
    }

    return { active, overlay, dimmed, dimmedActive };
  }, [dashes, getDashVisibility, floorVisibility?.mode]);

  // ========================================================================
  // 9. ОБНОВЛЕНИЕ INSTANCED MESH МАТРИЦ
  // ========================================================================

  // Хелпер для обновления InstancedMesh
  const updateInstancedMesh = useCallback(
    (
      ref: React.RefObject<THREE.InstancedMesh | null>,
      dashList: typeof dashes,
    ) => {
      const mesh = ref.current;
      if (!mesh) return;

      // Устанавливаем count даже если список пустой (для скрытия)
      mesh.count = dashList.length;

      if (dashList.length === 0) {
        mesh.instanceMatrix.needsUpdate = true;
        return;
      }

      // Используем кэшированные объекты вместо создания новых
      const tempMatrix = tempMatrixRef.current;
      const tempScale = tempScaleRef.current;

      for (let i = 0; i < dashList.length; i++) {
        const dash = dashList[i];
        tempScale.set(1, 1, dash.len);
        tempMatrix.compose(dash.position, dash.quaternion, tempScale);
        mesh.setMatrixAt(i, tempMatrix);
      }

      mesh.instanceMatrix.needsUpdate = true;
    },
    [],
  );

  // Эффект для обновления всех InstancedMesh при изменении дашей
  useEffect(() => {
    updateInstancedMesh(activeInstanceRef, groupedDashes.active);
    updateInstancedMesh(overlayInstanceRef, groupedDashes.overlay);
    updateInstancedMesh(dimmedInstanceRef, groupedDashes.dimmed);
    updateInstancedMesh(dimmedActiveInstanceRef, groupedDashes.dimmedActive);
  }, [groupedDashes, updateInstancedMesh]);

  // ========================================================================
  // 10. РЕНДЕРИНГ С INSTANCED MESH
  // ========================================================================

  if (dashes.length === 0) return null;

  // Максимальное количество инстансов (для аллокации буфера)
  const maxInstances = dashes.length;

  return (
    <group>
      {/* Активные даши (с bloom эффектом) */}
      {groupedDashes.active.length > 0 && (
        <instancedMesh
          ref={activeInstanceRef}
          args={[baseGeometry, activeMaterial, maxInstances]}
          frustumCulled={false}
        />
      )}

      {/* Затемнённые даши (обычные) */}
      {groupedDashes.dimmed.length > 0 && (
        <instancedMesh
          ref={dimmedInstanceRef}
          args={[baseGeometry, dimmedMaterial, maxInstances]}
          frustumCulled={false}
        />
      )}

      {/* Затемнённые активные даши (для floor_selected режима) */}
      {groupedDashes.dimmedActive.length > 0 && (
        <instancedMesh
          ref={dimmedActiveInstanceRef}
          args={[baseGeometry, dimmedActiveMaterial, maxInstances]}
          frustumCulled={false}
        />
      )}

      {/* Overlay слой - активные даши поверх всего (решает z-fighting) */}
      {/* Рендерим всегда, чтобы ref был стабильным для useEffect */}
      <group renderOrder={1000}>
        <instancedMesh
          ref={overlayInstanceRef}
          args={[baseGeometry, undefined, maxInstances]}
          material={overlayMaterial}
          frustumCulled={false}
          renderOrder={1000}
          visible={groupedDashes.overlay.length > 0}
        />
      </group>
    </group>
  );
});

export default RouteLine;
